import React, { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  AccordionDetails,
  Grid,
  CircularProgress,
  FormControl,
  DialogActions,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PageContainer from "../../../components/pageContainer";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { PictureAsPdf, TableView, ExpandMore, Add, Delete, FileUpload } from "@mui/icons-material";
import dayjs from "dayjs";
import useOrder from "../../../hooks/useOrder";
import { OrderResponse, PhotoSetResponse, UpdateOrderAdminOrdersOrderIdPutData, WorkItemResponse } from "../../../client";
import { downloadPdf, downloadExcel, downloadDefaultPdf } from "../../../utils/downloadUtils";
import PhoneNumberField from "../../../components/phoneNumberField";
import PostalCodeField from "../../../components/postalCodeField";
import useReport from "../../../hooks/useReport";
import ImageSelector from "../../../components/imageSelector";
import FixedAspectImage from "../../../components/fixedAspectImage";
import { compressImage } from "../../../utils/imageCompression";
import FloorPlanEditorWrapper from "../../../components/floorPlanEditorWrapper";
import { StyledAccordion, StyledAccordionSummary, StyledTableCell } from "../../../styles/commonStyles";
import { KEPT_FLOOR_PLAN_MESSAGE, keepFloorPlansWithBackgroundLoadError } from "../../../utils/floorPlanSave";
import { ImageChange, PhotoSetChanges, PhotoUpdate } from "../../../types/photo";
import { isValidDateValue } from "../../../utils/typeGuards";
import { imageSrcForLoad } from "../../../utils/imageUrl";


const OrderFormDetail: FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<OrderResponse | null>(null);
  const { id } = useParams();
  const { getOrderById, updateOrder, updateOrderStatus } = useOrder();
  const [photoChanges, setPhotoChanges] = useState<Map<number, PhotoSetChanges>>(new Map());
  const { deleteFile, getImageUploadUrl } = useReport();
  const idNum = Number(id);
  const [originalData, setOriginalData] = useState<OrderResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type?: 'work' | 'photo' | 'floor_plan';
    index: number;
  } | null>(null);
  const floorPlanEditorRefs = useRef<{ [key: number]: any }>({});




  useEffect(() => {
    if (idNum) {
      getData();
    }
  }, [idNum]);

  const getData = async () => {
    if (!idNum) return;
    try {
      const res = await getOrderById({ orderId: idNum });
      if (res) {
        setData(res);
        setOriginalData(res);
      }
    } catch (error) {
      console.error("Failed to get order detail", error);
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setData(JSON.parse(JSON.stringify(originalData)));
    }
    // 編集中に選択した未保存の写真変更(photoChanges)も破棄する。
    // クリアしないと、キャンセル後に再編集して保存した際に、
    // 前回キャンセルしたはずの画像がアップロードされてしまう。
    setPhotoChanges(new Map());
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    setOriginalData(JSON.parse(JSON.stringify(data)));
    setIsEditing(true);
  };

  const renderEditableField = (
    value: any,
    onChange: (value: any) => void,
    type: 'text' | 'number' = 'text'
  ) => {
    const validValue = isValidFieldValue(value) ? value : undefined;

    if (isEditing) {
      return (
        <TextField
          fullWidth
          value={validValue?.toString() || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          type={type}
        />
      );
    }
    return validValue?.toString() || '-';
  };



  const uploadPhoto = async (change: ImageChange): Promise<string | null> => {
    if (!change.newFile) return null;

    try {
      // 画像を圧縮（パラメータは関数内で固定）
      const compressResult = await compressImage(change.newFile);

      // 圧縮された画像ファイルを使用
      const compressedFile = compressResult.file;

      const uploadUrlResponse = await getImageUploadUrl({
        filename: change.newFile.name,
        folder: 'order'
      });

      if (!uploadUrlResponse?.upload_url) {
        console.error('No upload URL received');
        return null;
      }

      const { url, fields } = uploadUrlResponse.upload_url;
      // FormDataの作成
      const formData = new FormData();
      // 認証フィールドを追加
      Object.entries(fields).forEach(([key, value]) => {
        // value が string型であることを確認
        formData.append(key, value as string);
      });
      // 圧縮されたファイルを追加
      formData.append('file', compressedFile);

      // S3へのアップロード
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      // アップロード成功後のURLを構築
      const finalUrl = `${url}${fields.key}`;
      return finalUrl;

    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };


  // 写真セット情報の更新処理関数は同じ
  const getUpdatedPhotoSets = (photoSets: PhotoSetResponse[] | null | undefined): PhotoUpdate[] => {
    if (!photoSets) return [];


    return photoSets.map(photo => {
      const changes = photoChanges.get(photo.id);

      // before_image_path の処理
      let beforeImagePath: string | null = null;
      if (changes?.before?.newUrl && typeof changes.before.newUrl === 'string') {
        beforeImagePath = changes.before.newUrl;
      } else if (typeof photo.before_image_path === 'string') {
        if (photo.before_image_path.startsWith('https://')) {
          beforeImagePath = photo.before_image_path;
        } else {
          console.log('Invalid before URL format:', photo.before_image_path);
        }
      }

      // after_image_path の処理
      let afterImagePath: string | null = null;
      if (changes?.after?.newUrl && typeof changes.after.newUrl === 'string') {
        afterImagePath = changes.after.newUrl;
      } else if (typeof photo.after_image_path === 'string') {
        if (photo.after_image_path.startsWith('https://')) {
          afterImagePath = photo.after_image_path;
        } else {
          console.log('Invalid after URL format:', photo.after_image_path);
        }
      }

      const result = {
        title: photo.title || '',
        before_image_path: beforeImagePath,
        before_comment: photo.before_comment || '',
        after_image_path: afterImagePath,
        after_comment: photo.after_comment || ''
      };

      return result;
    });
  };

  const addPhotoSet = () => {
    if (!data || !data.photo_sets) return;
    const newPhotoSet = {
      id: Date.now(),
      title: "",
      before_image_path: null,
      before_comment: "",
      after_image_path: null,
      after_comment: ""
    };
    setData({ ...data, photo_sets: [...data.photo_sets, newPhotoSet] });
  };

  const handlePhotoChange = async (
    photoSet: PhotoSetResponse,
    type: 'before' | 'after',
    file: File | null,
    onChange: (updatedPhotoSet: PhotoSetResponse) => void  // onChange パラメータを追加
  ) => {
    const changes = photoChanges.get(photoSet.id) || {} as PhotoSetChanges;

    // S3のURLのみを保持し、Blob URLは保持しない
    const getValidS3Url = (url: string | null | undefined): string | null => {
      if (!url) return null;
      return url.startsWith('https://') ? url : null;
    };

    if (type === 'before') {
      changes.before = {
        oldUrl: getValidS3Url(photoSet.before_image_path),
        newFile: file,
        newUrl: null
      };
    } else {
      changes.after = {
        oldUrl: getValidS3Url(photoSet.after_image_path),
        newFile: file,
        newUrl: null
      };
    }

    setPhotoChanges(new Map(photoChanges.set(photoSet.id, changes)));

    // UIの更新用に一時的なBlob URLを生成
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      if (type === 'before') {
        onChange({
          ...photoSet,
          before_image_path: blobUrl
        });
      } else {
        onChange({
          ...photoSet,
          after_image_path: blobUrl
        });
      }
    }
  };

  // Blob URLのクリーンアップ用関数
  const cleanupBlobUrl = (url: string | null | undefined) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  // 画像削除時に、その写真セットの保留中の変更(未アップロードの新規画像)を破棄する。
  // これを呼ばずに写真だけnullにすると、新規選択→削除の順で操作した際にphotoChangesに
  // 残った新規ファイルがsubmit時にアップロードされ、削除したはずの画像が復活してしまう。
  const clearPhotoChange = (photoSetId: number, type: 'before' | 'after') => {
    setPhotoChanges(prev => {
      const changes = prev.get(photoSetId);
      if (!changes || !changes[type]) return prev;
      const next = new Map(prev);
      const updated = { ...changes };
      delete updated[type];
      next.set(photoSetId, updated);
      return next;
    });
  };

  const renderPhotoSetContent = (
    photoSet: PhotoSetResponse,
    onChange: (updatedPhotoSet: PhotoSetResponse) => void
  ) => {
    // 画像のアスペクト比を設定 (4:3)
    const imageAspectRatio = 4 / 3; // ≈ 1.33
    // 画像表示の統一サイズ設定
    const imageDisplayHeight = 240; // 高さを固定値に設定（ピクセル）

    return (
      <Box>
        <Grid container spacing={2}>
          {/* Before写真エリア */}
          <Grid item xs={12} md={6}>
            <Box>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.before_image_path}
                  alt="image1"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.before_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'before', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        before_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.before_image_path);
                      clearPhotoChange(photoSet.id, 'before');
                      onChange({
                        ...photoSet,
                        before_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="コメント"
                  value={photoSet.before_comment || ''}
                  onChange={(e) => {
                    onChange({
                      ...photoSet,
                      before_comment: e.target.value
                    });
                  }}
                  size="small"
                />
              ) : (
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    コメント
                  </Typography>
                  <Typography>
                    {photoSet.before_comment || '-'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* After写真エリア */}
          <Grid item xs={12} md={6}>
            <Box>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.after_image_path}
                  alt="image2"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.after_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'after', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        after_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.after_image_path);
                      clearPhotoChange(photoSet.id, 'after');
                      onChange({
                        ...photoSet,
                        after_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="コメント"
                  value={photoSet.after_comment || ''}
                  onChange={(e) => {
                    onChange({
                      ...photoSet,
                      after_comment: e.target.value
                    });
                  }}
                  size="small"
                />
              ) : (
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    コメント
                  </Typography>
                  <Typography>
                    {photoSet.after_comment || '-'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };


  const renderSectionFooter = (onAdd: () => void, buttonText: string) => (
    isEditing && (
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={onAdd}
          startIcon={<Add />}
          size="small"
        >
          {buttonText}
        </Button>
      </Box>
    )
  );




  const renderEditableDate = (
    value: string | number | null | undefined,
    onChange: (date: dayjs.Dayjs | null) => void
  ) => {
    if (isEditing) {
      const dateValue = isValidDateValue(value) ? dayjs(value) : null;
      return (
        <DatePicker
          value={dateValue}
          onChange={onChange}
          format="YYYY/MM/DD"
          slotProps={{ textField: { size: 'small' } }}
        />
      );
    }
    return isValidDateValue(value) ? dayjs(value).format('YYYY/MM/DD') : '-';
  };

  // 時間フィールドのレンダリング
  const renderEditableTime = (
    value: string | null | undefined,
    onChange: (time: dayjs.Dayjs | null) => void
  ) => {
    if (isEditing) {
      const timeValue = value ? dayjs(value, "HH:mm:ss") : null;
      return (
        <TimePicker
          value={timeValue}
          onChange={onChange}
          ampm={false}
          format="HH:mm"
          slotProps={{ textField: { size: 'small' } }}
        />
      );
    }
    return value ? dayjs(value, "HH:mm:ss").format('HH:mm') : '-';
  };


  // 保存処理を更新
  const submit = async () => {
    if (!data || !idNum) return;

    setIsLoading(true);

    try {
      // 重要な問題回避策: データを直接操作する前にディープコピーを作成
      const workingData = JSON.parse(JSON.stringify(data));

      // 背景画像を読み込めなかった見取り図は、撮り直すと背景の無い画像で置き換わるため、
      // 編集前のまま残す（撮り直し・削除をしない。utils/floorPlanSave.ts）
      let keptFloorPlanIds = new Set<number>();

      // 1. 見取り図の処理を追加
      if (workingData.floor_plans) {
        keptFloorPlanIds = keepFloorPlansWithBackgroundLoadError(
          workingData.floor_plans,
          originalData?.floor_plans,
          floorPlanEditorRefs.current
        );

        // 1-A: 線の確定処理 - 各エディターに線の確定を要求
        const confirmPromises = [];

        for (let i = 0; i < workingData.floor_plans.length; i++) {
          const plan = workingData.floor_plans[i];
          if (keptFloorPlanIds.has(plan.id)) continue;

          const editorRef = floorPlanEditorRefs.current[plan.id];
          if (editorRef && editorRef.confirmCurrentLine) {
            try {
              const confirmPromise = editorRef.confirmCurrentLine()
                .then(() => {
                  return true;
                })
                .catch((error: any) => {
                  console.error(`見取り図 ${i + 1} (ID: ${plan.id}) の線確定処理でエラーが発生しました:`, error);
                  return false;
                });

              confirmPromises.push(confirmPromise);
            } catch (error) {
              console.error(`見取り図 ${i + 1} (ID: ${plan.id}) の線確定メソッド呼び出しでエラーが発生しました:`, error);
            }
          }
        }

        if (confirmPromises.length > 0) {
          await Promise.all(confirmPromises);
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // 1-B: JSONデータ取得 - 各エディターから最新のJSONデータを取得
        for (let i = 0; i < workingData.floor_plans.length; i++) {
          const plan = workingData.floor_plans[i];
          if (keptFloorPlanIds.has(plan.id)) continue;

          const editorRef = floorPlanEditorRefs.current[plan.id];
          if (editorRef && editorRef.getCurrentJsonData) {
            try {
              const currentJsonData = editorRef.getCurrentJsonData();
              if (currentJsonData) {
                plan.json_data = currentJsonData;

                // 元のdataオブジェクトのfloor_plansも更新
                if (data.floor_plans && data.floor_plans[i]) {
                  data.floor_plans[i].json_data = currentJsonData;
                }
              }
            } catch (error) {
              console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のJSONデータの取得でエラーが発生しました:`, error);
            }
          }

          // 各見取り図の処理間に少し間隔を開ける
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // 1-C: キャプチャ処理 - 各エディターのキャンバスをキャプチャしてアップロード
        for (let i = 0; i < workingData.floor_plans.length; i++) {
          const plan = workingData.floor_plans[i];
          if (keptFloorPlanIds.has(plan.id)) continue;

          // 旧形式のデータでなければキャプチャを更新
          const isLegacyData = Boolean(
            plan.image_data &&
            !plan.background_image_url &&
            !plan.floor_plan_image_url
          );

          if (!isLegacyData) {
            // 既存のキャプチャURLがあれば削除
            if (plan.floor_plan_image_url && plan.floor_plan_image_url.startsWith('https://')) {
              try {
                await deleteFile({ fileUrl: plan.floor_plan_image_url });
              } catch (deleteError) {
                console.error(`見取り図 ${i + 1} (ID: ${plan.id}) の既存の画像削除に失敗:`, deleteError);
              }
            }

            // キャプチャ処理
            const editorRef = floorPlanEditorRefs.current[plan.id];
            if (editorRef && editorRef.captureCanvas) {
              try {
                await new Promise(resolve => setTimeout(resolve, 300));

                const capturedBlob = await editorRef.captureCanvas();

                if (capturedBlob) {
                  // S3アップロード
                  const uploadUrlResponse = await getImageUploadUrl({
                    filename: `floor_plan_${plan.id || Date.now()}.jpg`,
                    folder: 'floor_plans'
                  });

                  if (uploadUrlResponse?.upload_url) {
                    const { url, fields } = uploadUrlResponse.upload_url;

                    const formData = new FormData();
                    Object.entries(fields).forEach(([key, value]) => {
                      formData.append(key, value as string);
                    });
                    formData.append('file', capturedBlob);

                    const response = await fetch(url, {
                      method: 'POST',
                      body: formData
                    });

                    if (response.ok) {
                      const newUrl = `${url}${fields.key}`;
                      plan.floor_plan_image_url = newUrl;

                      // 元のdataオブジェクトのfloor_plansも更新
                      if (data.floor_plans && data.floor_plans[i]) {
                        data.floor_plans[i].floor_plan_image_url = newUrl;
                      }
                    } else {
                      console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のアップロードに失敗:`, response.status, response.statusText);
                      await createFallbackCapture(plan);
                    }
                  }
                } else {
                  console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のキャプチャに失敗しました`);
                  await createFallbackCapture(plan);
                }
              } catch (error) {
                console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のキャプチャ処理でエラー:`, error);
                await createFallbackCapture(plan);
              }
            } else {
              console.warn(`見取り図 ${i + 1} (ID: ${plan.id}) のエディタ参照がありません（キャプチャ）`);
              await createFallbackCapture(plan);
            }
          }
        }
      }

      // 既存の写真処理は変更なし (1. 写真の更新処理)
      for (const [photoSetId, changes] of Array.from(photoChanges.entries())) {
        // Before画像の処理
        if (changes.before?.newFile) {
          // 古い画像の削除
          if (changes.before.oldUrl) {
            await deleteFile({ fileUrl: changes.before.oldUrl });
          }

          changes.before.newUrl = await uploadPhoto(changes.before);
        }

        // After画像の処理
        if (changes.after?.newFile) {
          // 古い画像の削除
          if (changes.after.oldUrl) {
            await deleteFile({ fileUrl: changes.after.oldUrl });
          }

          changes.after.newUrl = await uploadPhoto(changes.after);
        }
      }

      // updateDataオブジェクトを作成する際に、floor_plansを追加
      const updateData: UpdateOrderAdminOrdersOrderIdPutData = {
        orderId: idNum,
        requestBody: {
          representative_name: data.representative_name || '',
          representative_furigana: data.representative_furigana || '',
          representative_phone: data.representative_phone || '',
          customer_name: data.customer_name || '',
          customer_furigana: data.customer_furigana || '',
          customer_rep_name: data.customer_rep_name || '',
          customer_rep_furigana: data.customer_rep_furigana || '',
          customer_address: data.customer_address || '',
          customer_post_number: data.customer_post_number || '',
          store_name: data.store_name || '',
          fixed_phone: data.fixed_phone || '',
          mobile_phone: data.mobile_phone || '',
          site_address: data.site_address || '',
          business_type: data.business_type || '',
          preferred_date_1: data.preferred_date_1 || null,
          preferred_start_time_1: data.preferred_start_time_1 || null,
          preferred_end_time_1: data.preferred_end_time_1 || null,
          preferred_date_2: data.preferred_date_2 || null,
          preferred_start_time_2: data.preferred_start_time_2 || null,
          preferred_end_time_2: data.preferred_end_time_2 || null,
          date_request_reason: data.date_request_reason || '',
          memo: data.memo || '',
          key_status: data.key_status || '',
          key_handling_method: data.key_handling_method || '',
          parking_status: data.parking_status || '',
          parking_fee: data.parking_fee || '',
          supervision_status: data.supervision_status || '',
          supervisor_type: data.supervisor_type || '',
          supervision_timing: data.supervision_timing || '',
          breaker_status: data.breaker_status || '',
          breaker_details: data.breaker_details || '',
          waste_location: data.waste_location || '',
          utilities: data.utilities || '',
          work_date: data.work_date || null,
          work_start_time: data.work_start_time || null,
          contractor_name: data.contractor_name || '',
          amount: data.amount || '',
          work_items: data.work_items || [],
          status: data.status || '',

          // 写真セット情報
          photo_sets: getUpdatedPhotoSets(data.photo_sets),

          // 見取り図情報 - image_dataを更新せず、元のデータを維持
          floor_plans: workingData.floor_plans?.map((plan: { id: number; background_image_url: any; floor_plan_image_url: any; title: any; json_data: any; }) => {
            // 元のimage_dataを取得（または空文字列）
            const originalImageData = originalData?.floor_plans?.find(p => p.id === plan.id)?.image_data || '';

            // 旧形式のデータかどうかを判定
            const isLegacyData = Boolean(
              originalImageData &&
              !plan.background_image_url &&
              !plan.floor_plan_image_url
            );

            // 旧形式のデータの場合は、floor_plan_image_urlを含めない
            if (isLegacyData) {
              return {
                title: plan.title || '',
                json_data: plan.json_data || '',
                image_data: originalImageData,  // 元のimage_dataを使用
                background_image_url: null
                // floor_plan_image_urlは含めない
              };
            }

            // 通常の新形式データの場合
            return {
              title: plan.title || '',
              json_data: plan.json_data || '',
              image_data: originalImageData,  // 元のimage_dataを使用
              background_image_url: plan.background_image_url || null,
              floor_plan_image_url: plan.floor_plan_image_url || null
            };
          }) || [],

        }
      };

      const response = await updateOrder(updateData);

      if (response) {
        setIsEditing(false);
        await getData();
        if (keptFloorPlanIds.size > 0) {
          alert(KEPT_FLOOR_PLAN_MESSAGE);
        }
      } else {
        alert("更新に失敗しました。再度お試しください。");
      }

    } catch (error) {
      console.error("Failed to update order", error);
      alert("更新に失敗しました。再度お試しください。");
    } finally {
      setIsLoading(false);
    }
  };

  const isValidFieldValue = (value: any): value is string | number | boolean | null | undefined => {
    return (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null ||
      value === undefined
    );
  };

  const addWorkItem = () => {
    if (!data || !data.work_items) {
      // データがない場合は何もしない
      if (!data?.id || !data?.created_at) {
        return;
      }

      setData({
        ...data,
        work_items: [],
        status: data.status,
        id: data.id,
        created_at: data.created_at,
        updated_at: data.updated_at
      } as OrderResponse);
      return;
    }

    // WorkItemResponseの型に合わせて必要なフィールドを追加
    const newWorkItem: WorkItemResponse = {
      id: Date.now(),
      order_id: data.id,
      name: "",
      content: "",
      quantity: null,
      unit: null,
      created_at: new Date().toISOString()
    };

    const newWorkItems = [...data.work_items, newWorkItem];

    // 必須フィールドを含めてデータを更新
    setData({
      ...data,
      work_items: newWorkItems,
      status: data.status,
      id: data.id,
      created_at: data.created_at,
      updated_at: data.updated_at
    } as OrderResponse);

    // 新しい作業項目のアコーディオンを展開
    setExpandedUnits(prev => ({
      ...prev,
      [`work-${newWorkItem.id}`]: true
    }));
  };


  const handleDeleteClick = (type: 'work' | 'photo' | 'floor_plan', index: number) => {
    setDeleteTarget({ type, index });
    setDeleteDialogOpen(true);
  };

  const SectionHeader = ({ index, type = 'work' }: {
    index: number,
    type?: 'work' | 'photo' | 'floor_plan'
  }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
    >
      <Typography>
        {type === 'photo'
          ? `写真セット ${index + 1}`
          : type === 'floor_plan'
            ? `見取り図 ${index + 1}`
            : `作業項目 ${index + 1}`}
      </Typography>
      {isEditing && (
        <Button
          startIcon={<Delete />}
          color="error"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(type, index);
          }}
          sx={{ ml: 2 }}
        >
          削除
        </Button>
      )}
    </Box>
  );


  // フォールバックとして見取り図のキャプチャを作成する関数
  const createFallbackCapture = async (plan: any) => {
    // 新しいキャンバスを作成
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 250;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // 背景色を白に設定
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // グリッドを描画（マス目の背景）
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.lineWidth = 1;

      const gridSize = 25;
      for (let i = 0; i <= canvas.width; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }

      for (let i = 0; i <= canvas.height; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
        ctx.stroke();
      }

      // 背景画像があれば描画
      if (plan.background_image_url) {
        // 背景画像をロードして描画
        try {
          const img = new Image();
          // エディタと同じく CORS 付きで読む。付けないと canvas が汚れて撮影（toBlob）が失敗するうえ、
          // CORS なしの応答（S3 は Vary を付けない）がキャッシュに入り、同じ署名付き URL をエディタが
          // CORS 付きで読み直したときに失敗して背景が消える
          img.crossOrigin = "anonymous";

          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve();
            };
            img.onerror = (e) => {
              console.error('背景画像のロードに失敗:', e);
              resolve(); // エラーでも続行
            };
            // 署名の無い URL にだけキャッシュ回避のタイムスタンプを足す（署名付き URL は足すと 403）
            img.src = imageSrcForLoad(plan.background_image_url);

            // 3秒後にタイムアウト
            setTimeout(() => {
              if (!img.complete) {
                console.warn('画像のロードがタイムアウトしました');
                resolve();
              }
            }, 3000);
          });
        } catch (err) {
          console.error('背景画像の描画でエラー:', err);
        }
      }
      // image_dataがある場合はそれを描画（background_image_urlの代わり）
      else if (plan.image_data) {
        try {
          const img = new Image();

          await new Promise<void>((resolve) => {
            img.onload = () => {
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve();
            };
            img.onerror = () => {
              console.error('image_dataからの画像ロードに失敗');
              resolve(); // エラーでも続行
            };
            img.src = `data:image/jpeg;base64,${plan.image_data}`;

            // 3秒後にタイムアウト
            setTimeout(() => {
              if (!img.complete) {
                console.warn('image_dataからの画像ロードがタイムアウトしました');
                resolve();
              }
            }, 3000);
          });
        } catch (err) {
          console.error('image_dataからの画像描画でエラー:', err);
        }
      }

      // キャンバスをBlobに変換してアップロード
      return new Promise<void>((resolve) => {
        canvas.toBlob(async (blob) => {
          if (blob) {
            await uploadBlobToS3(blob, plan);
          } else {
            console.error('Blobの生成に失敗しました');
          }
          resolve();
        }, 'image/jpeg', 0.9);
      });
    }
  };

  // Blobをアップロードする共通関数（createFallbackCaptureから呼び出される）
  const uploadBlobToS3 = async (blob: Blob, plan: any) => {
    // S3アップロードURLを取得
    const uploadUrlResponse = await getImageUploadUrl({
      filename: `floor_plan_${plan.id || Date.now()}.jpg`,
      folder: 'floor_plans'
    });

    if (uploadUrlResponse?.upload_url) {
      const { url, fields } = uploadUrlResponse.upload_url;

      // FormDataの作成
      const formData = new FormData();
      // 認証フィールドを追加
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      // Blobを追加
      formData.append('file', blob);

      // S3へのアップロード
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // アップロード成功後のURLを構築
        const newUrl = `${url}${fields.key}`;
        plan.floor_plan_image_url = newUrl;
      } else {
        console.error('Floor plan image upload failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    }
  };


  // 作業項目のアコーディオンの展開状態を制御
  const handleUnitAccordionChange = (unitId: React.Key | null | undefined) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (unitId !== undefined && unitId !== null) {
        setExpandedUnits(prev => ({ ...prev, [`work-${unitId}`]: isExpanded }));
      }
    };

  const handleDelete = () => {
    if (!deleteTarget || !data) return;

    switch (deleteTarget.type) {
      case 'photo':
        if (data.photo_sets) {
          const removedPhoto = data.photo_sets[deleteTarget.index];
          const newPhotos = [...data.photo_sets];
          newPhotos.splice(deleteTarget.index, 1);
          setData({ ...data, photo_sets: newPhotos });
          // 削除した写真セットの保留中の変更(未アップロードの新規画像等)も破棄する。
          // 消さないとsubmit時にphotoChangesを直接走査してしまい、削除したはずの
          // 写真がアップロードされてしまう。
          if (removedPhoto) {
            setPhotoChanges(prev => {
              const next = new Map(prev);
              next.delete(removedPhoto.id);
              return next;
            });
          }
        }
        break;
      case 'floor_plan':
        if (data.floor_plans) {
          const newFloorPlans = [...data.floor_plans];
          newFloorPlans.splice(deleteTarget.index, 1);
          setData({ ...data, floor_plans: newFloorPlans });
        }
        break;
      case 'work':
        if (data.work_items) {
          const newWorkItems = [...data.work_items];
          newWorkItems.splice(deleteTarget.index, 1);
          setData({
            ...data,
            work_items: newWorkItems,
            status: data.status,
            id: data.id,
            created_at: data.created_at,
            updated_at: data.updated_at
          } as OrderResponse);
        }
        break;
    }
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };


  // 削除ダイアログのコンポーネント
  const DeleteConfirmDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
    >
      <DialogTitle>削除の確認</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {deleteTarget && `この${deleteTarget.type === 'photo'
            ? '写真'
            : deleteTarget.type === 'work'
              ? '作業項目'
              : '見取り図'
            }を削除してもよろしいですか？`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
        <Button onClick={handleDelete} color="error">削除</Button>
      </DialogActions>
    </Dialog>
  );


  // Excelダウンロードの処理
  const handleDownloadExcel = async () => {
    if (!data) return;

    await downloadExcel(
      {
        id: idNum,
        destinationName: data.customer_name || data.store_name,
        workDate: data.work_date,
        setLoading: setIsLoading
      },
      'order'
    );
  };


  const handleDownloadDefaultPdf = async () => {
    if (!data) return;

    await downloadDefaultPdf(
      {
        id: idNum,
        destinationName: data.customer_name || data.store_name,
        workDate: data.work_date,
        setLoading: setIsLoading
      },
      'order'
    );
  };


  // 既存のhandleDownloadPdf関数を以下に置き換え
  const handleDownloadPdf = async () => {
    if (!data) return;

    await downloadPdf(
      {
        id: idNum,
        destinationName: data.customer_name || data.store_name,
        workDate: data.work_date,
        setLoading: setIsLoading
      },
      'order'
    );
  };

  return (
    <PageContainer>
      <DeleteConfirmDialog />
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {!data ? (
        <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {/* ヘッダー */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="div">受注書詳細</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {isEditing ? (
                <><Button variant="outlined" onClick={handleCancel}>
                  キャンセル
                </Button>
                  <Button variant="contained" color="primary" onClick={submit}>
                    保存
                  </Button>
                </>
              ) : (
                <>
                  <Box display="flex" gap={1}>
                    <TextField
                      select
                      value={data?.status}
                      onChange={async (e) => {
                        try {
                          await updateOrderStatus({
                            orderId: idNum,
                          });
                          setData((prevData: any) =>
                            prevData ? {
                              ...prevData,
                              status: e.target.value
                            } : null
                          );
                        } catch (error) {
                          console.error("ステータス更新に失敗しました:", error);
                          alert("ステータスの更新に失敗しました。再度お試しください。");
                        }
                      }}
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiSelect-select': {
                          color: data?.status === "未対応" ? 'warning.main' : 'success.main',
                          fontWeight: 'bold'
                        }
                      }}
                    >
                      <MenuItem value="未対応">未対応</MenuItem>
                      <MenuItem value="対応済み">対応済み</MenuItem>
                    </TextField>
                    {data.pdf_url && (
                      <Button
                        startIcon={<FileUpload />}
                        variant="outlined"
                        onClick={handleDownloadDefaultPdf}
                      >
                        現場提出時PDF出力
                      </Button>
                    )}
                    <Button
                      startIcon={<PictureAsPdf />}
                      variant="outlined"
                      onClick={handleDownloadPdf}
                    >
                      PDF出力
                    </Button>
                    <Button
                      startIcon={<TableView />}
                      variant="outlined"
                      onClick={handleDownloadExcel}
                    >
                      Excel出力
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleStartEditing}>
                      編集
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* 基本情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">基本情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  {/* 担当者情報 */}
                  <TableRow>
                    <StyledTableCell colSpan={2} className="section-header">
                      担当者情報
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      担当者名
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.representative_name,
                        (value) => setData({ ...data, representative_name: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      担当者名（ふりがな）
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.representative_furigana,
                        (value) => setData({ ...data, representative_furigana: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      担当者電話番号
                    </StyledTableCell>
                    <TableCell>
                      <PhoneNumberField
                        value={data.representative_phone || ""}
                        onChange={(value) =>
                          setData({ ...data, representative_phone: value })
                        }
                        isEditing={isEditing}
                        maxLength={13} // 国際電話番号対応
                      />
                    </TableCell>
                  </TableRow>

                  {/* サイン画像 */}

                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      サイン
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" gap={2} alignItems="center">

                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">担当者</Typography>
                          {data.manager_signature ? (
                            <img
                              src={`data:image/jpeg;base64,${data.manager_signature}`}
                              alt="Manager Signature"
                              style={{ width: '200px', height: '100px', objectFit: 'contain', border: '1px solid #ccc' }}
                            />
                          ) : (
                            <Typography color="textSecondary">未登録</Typography>
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">承認者</Typography>
                          {data.approver_signature ? (
                            <img
                              src={`data:image/jpeg;base64,${data.approver_signature}`}
                              alt="Manager Signature"
                              style={{ width: '200px', height: '100px', objectFit: 'contain', border: '1px solid #ccc' }}
                            />
                          ) : (
                            <Typography color="textSecondary">未登録</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* 顧客情報 */}
                  <TableRow>
                    <StyledTableCell colSpan={2} className="section-header">
                      顧客情報
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      顧客名
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.customer_name,
                        (value) => setData({ ...data, customer_name: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      顧客名（ふりがな）
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.customer_furigana,
                        (value) => setData({ ...data, customer_furigana: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      顧客担当者名
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.customer_rep_name,
                        (value) => setData({ ...data, customer_rep_name: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      顧客担当者名（ふりがな）
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.customer_rep_furigana,
                        (value) => setData({ ...data, customer_rep_furigana: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      郵便番号
                    </StyledTableCell>
                    <TableCell>
                      <PostalCodeField
                        value={data.customer_post_number || ""}
                        onChange={(value) =>
                          setData({ ...data, customer_post_number: value })
                        }
                        isEditing={isEditing}
                        maxLength={7} // 日本の郵便番号対応
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      住所
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.customer_address,
                        (value) => setData({ ...data, customer_address: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      店舗名
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.store_name,
                        (value) => setData({ ...data, store_name: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      固定電話
                    </StyledTableCell>
                    <TableCell>
                      <PhoneNumberField
                        value={data.fixed_phone || ""}
                        onChange={(value) =>
                          setData({ ...data, fixed_phone: value })
                        }
                        isEditing={isEditing}
                        maxLength={13}
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      携帯電話
                    </StyledTableCell>
                    <TableCell>
                      <PhoneNumberField
                        value={data.mobile_phone || ""}
                        onChange={(value) =>
                          setData({ ...data, mobile_phone: value })
                        }
                        isEditing={isEditing}
                        maxLength={13}
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      現場住所
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.site_address,
                        (value) => setData({ ...data, site_address: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      業種
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.business_type,
                        (value) => setData({ ...data, business_type: value })
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">日時</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      第1希望
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" gap={2} alignItems="center">
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">日付</Typography>
                          {renderEditableDate(
                            data.preferred_date_1,
                            (newDate) => setData({
                              ...data,
                              preferred_date_1: newDate ? newDate.format('YYYY-MM-DD') : null
                            })
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">開始時間</Typography>
                          {renderEditableTime(
                            data.preferred_start_time_1,
                            (newTime) => setData({
                              ...data,
                              preferred_start_time_1: newTime ? newTime.format('HH:mm:ss') : null
                            })
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">終了時間</Typography>
                          {renderEditableTime(
                            data.preferred_end_time_1,
                            (newTime) => setData({
                              ...data,
                              preferred_end_time_1: newTime ? newTime.format('HH:mm:ss') : null
                            })
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      第2希望
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" gap={2} alignItems="center">
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">日付</Typography>
                          {renderEditableDate(
                            data.preferred_date_2,
                            (newDate) => setData({
                              ...data,
                              preferred_date_2: newDate ? newDate.format('YYYY-MM-DD') : null
                            })
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">開始時間</Typography>
                          {renderEditableTime(
                            data.preferred_start_time_2,
                            (newTime) => setData({
                              ...data,
                              preferred_start_time_2: newTime ? newTime.format('HH:mm:ss') : null
                            })
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">終了時間</Typography>
                          {renderEditableTime(
                            data.preferred_end_time_2,
                            (newTime) => setData({
                              ...data,
                              preferred_end_time_2: newTime ? newTime.format('HH:mm:ss') : null
                            })
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業日要望理由
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.date_request_reason,
                        (value) => setData({ ...data, date_request_reason: value })
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>


          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">現地調査写真</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {data.photo_sets?.map((photo, index) => (
                <Box key={photo.id} mb={index < data.photo_sets!.length - 1 ? 4 : 0}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell colSpan={2} className="section-header">
                          <SectionHeader index={index} type="photo" />
                        </StyledTableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}>
                          {renderPhotoSetContent(photo, (updatedPhotoSet) => {
                            const newPhotos = [...data.photo_sets!];
                            newPhotos[index] = updatedPhotoSet;
                            setData({ ...data, photo_sets: newPhotos });
                          })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              ))}
              {renderSectionFooter(addPhotoSet, "現地調査写真を追加")}
            </AccordionDetails>
          </StyledAccordion>


          {/* 見取り図情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">見取り図情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {data.floor_plans?.map((plan, index) => (
                <Box key={plan.id} mb={index < data.floor_plans!.length - 1 ? 4 : 0}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell colSpan={2} className="section-header">
                          <SectionHeader index={index} type="floor_plan" />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell component="th" scope="row" className="item-label">
                          見取り図データ
                        </StyledTableCell>
                        <TableCell>
                          {isEditing ? (
                            <FloorPlanEditorWrapper
                              key={`floor-plan-${plan.id}`}
                              initialData={{
                                id: plan.id, // 明示的にidを渡す
                                title: plan.title || '',
                                json_data: plan.json_data || null,
                                image_data: plan.image_data,
                                background_image_url: plan.background_image_url,
                                floor_plan_image_url: plan.floor_plan_image_url
                              }}
                              editorRef={(ref) => {
                                if (plan.id && ref) {
                                  // 参照が変更された場合のみ更新
                                  if (floorPlanEditorRefs.current[plan.id] !== ref) {
                                    floorPlanEditorRefs.current[plan.id] = ref;
                                  }
                                }
                              }}
                              // フォームの更新部分を関数形式に変更し、ディープコピーを使用
                              onChange={(updatedPlan) => {
                                // まず最新の状態を取得するために関数形式でsetDataを使用
                                setData(prevData => {
                                  if (!prevData || !prevData.floor_plans) return prevData;

                                  // floor_plansのディープコピーを作成（重要）
                                  const newFloorPlans = JSON.parse(JSON.stringify(prevData.floor_plans));

                                  // 更新対象のプランを特定
                                  const planIndex = newFloorPlans.findIndex((p: { id: number; }) => p.id === plan.id);
                                  if (planIndex === -1) return prevData;


                                  // 対象プランの更新（新しいコピーを作成）
                                  newFloorPlans[planIndex] = {
                                    ...newFloorPlans[planIndex], // 既存のプロパティを維持
                                    title: updatedPlan.title,
                                    json_data: updatedPlan.json_data, // 更新されたJSONデータ
                                    background_image_url: updatedPlan.background_image_url,
                                    floor_plan_image_url: updatedPlan.floor_plan_image_url
                                  };

                                  // 新しい状態を返す（immutableに保つ）
                                  return {
                                    ...prevData,
                                    floor_plans: newFloorPlans
                                  };
                                });
                              }}
                            />
                          ) : (
                            // floor_plan_image_urlを優先して表示し、なければimage_dataを表示
                            plan.floor_plan_image_url ? (
                              <img
                                src={plan.floor_plan_image_url}
                                alt={plan.title ?? ''}
                                style={{ maxWidth: '600px', maxHeight: '250px' }}
                              />
                            ) : plan.image_data && (
                              <img
                                src={`data:image/jpeg;base64,${plan.image_data}`}
                                alt={plan.title ?? ''}
                                style={{ maxWidth: '600px', maxHeight: '250px' }}
                              />
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              ))}
              {/* 見取り図を追加するボタン */}
              {isEditing && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      const newPlan = {
                        id: Date.now(),
                        title: "見取り図 " + (data.floor_plans?.length ? data.floor_plans.length + 1 : 1),
                        json_data: JSON.stringify([]),
                        image_data: null
                      };
                      setData({ ...data, floor_plans: [...(data.floor_plans || []), newPlan] });
                    }}
                    startIcon={<Add />}
                    size="small"
                  >
                    見取り図を追加
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">作業項目</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {/* 作業項目リスト */}
              {data?.work_items && data.work_items.length > 0 ? (
                data.work_items.map((item: WorkItemResponse, index: number) => {
                  const workItemsLength = data.work_items?.length || 0;

                  return (
                    <StyledAccordion
                      key={item.id}
                      expanded={!!expandedUnits[`work-${item.id}`]}
                      onChange={handleUnitAccordionChange(item.id)}
                      sx={{ mb: index < workItemsLength - 1 ? 2 : 0 }}
                    >
                      <StyledAccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader index={index} type="work" />
                      </StyledAccordionSummary>
                      <AccordionDetails>
                        <Table>
                          <TableBody>
                            <TableRow>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                作業名称
                              </StyledTableCell>
                              <TableCell>
                                {renderEditableField(
                                  item.name,
                                  (value) => {
                                    if (data.work_items) {
                                      const newItems = [...data.work_items];
                                      newItems[index] = { ...item, name: value };
                                      setData({ ...data, work_items: newItems });
                                    }
                                  }
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                作業内容
                              </StyledTableCell>
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={item.content || ''}
                                    onChange={(e) => {
                                      if (data.work_items) {
                                        const newItems = [...data.work_items];
                                        newItems[index] = { ...item, content: e.target.value };
                                        setData({ ...data, work_items: newItems });
                                      }
                                    }}
                                    size="small"
                                  />
                                ) : (
                                  item.content || '-'
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                数量
                              </StyledTableCell>
                              <TableCell>
                                {renderEditableField(
                                  item.quantity,
                                  (value) => {
                                    if (data.work_items) {
                                      const newItems = [...data.work_items];
                                      newItems[index] = { ...item, quantity: Number(value) };
                                      setData({ ...data, work_items: newItems });
                                    }
                                  },
                                  'number'
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                単位
                              </StyledTableCell>
                              <TableCell>
                                {renderEditableField(
                                  item.unit,
                                  (value) => {
                                    if (data.work_items) {
                                      const newItems = [...data.work_items];
                                      newItems[index] = { ...item, unit: value };
                                      setData({ ...data, work_items: newItems });
                                    }
                                  }
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </StyledAccordion>
                  );
                })
              ) : null}

              {/* 作業項目追加ボタン */}
              {isEditing && (
                <Box display="flex" justifyContent="flex-end" mt={2} mb={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={addWorkItem}
                    startIcon={<Add />}
                    size="small"
                  >
                    作業項目を追加
                  </Button>
                </Box>
              )}

              {/* メモ欄 */}
              <Table sx={{ mt: 3 }}>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      メモ
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={data.memo || ''}
                          onChange={(e) => setData({ ...data, memo: e.target.value })}
                          size="small"
                        />
                      ) : (
                        data.memo || '-'
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* 作業条件 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">必要確認事項(必須)</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  {/* 鍵の情報 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      鍵預り
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl>
                          <RadioGroup
                            row
                            value={data.key_status || ''}
                            onChange={(e) => setData({ ...data, key_status: e.target.value })}
                          >
                            <FormControlLabel value="有り" control={<Radio />} label="有り" />
                            <FormControlLabel value="無し" control={<Radio />} label="無し" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        data.key_status || '-'
                      )}
                    </TableCell>
                  </TableRow>
                  {data.key_status === '有り' && (
                    <TableRow>
                      <StyledTableCell component="th" scope="row" className="item-label">
                        鍵の受渡・返却方法
                      </StyledTableCell>
                      <TableCell>
                        {renderEditableField(
                          data.key_handling_method,
                          (value) => setData({ ...data, key_handling_method: value })
                        )}
                      </TableCell>
                    </TableRow>
                  )}


                  {/* 駐車場情報 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      駐車場
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl>
                          <RadioGroup
                            row
                            value={data.parking_status || ''}
                            onChange={(e) => setData({ ...data, parking_status: e.target.value })}
                          >
                            <FormControlLabel value="有り" control={<Radio />} label="有り" />
                            <FormControlLabel value="無し" control={<Radio />} label="無し" />
                            <FormControlLabel value="有料有り" control={<Radio />} label="有料有り" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        data.parking_status || '-'
                      )}
                    </TableCell>
                  </TableRow>
                  {data.parking_status === '有料有り' && (
                    <TableRow>
                      <StyledTableCell component="th" scope="row" className="item-label">
                        有料駐車場代の負担
                      </StyledTableCell>
                      <TableCell>
                        {renderEditableField(
                          data.parking_fee,
                          (value) => setData({ ...data, parking_fee: value })
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* 立会い情報 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      立会い
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl>
                          <RadioGroup
                            row
                            value={data.supervision_status || ''}
                            onChange={(e) => setData({ ...data, supervision_status: e.target.value })}
                          >
                            <FormControlLabel value="有り" control={<Radio />} label="有り" />
                            <FormControlLabel value="無し" control={<Radio />} label="無し" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        data.supervision_status || '-'
                      )}
                    </TableCell>
                  </TableRow>
                  {data.supervision_status === '有り' && (
                    <>
                      <TableRow>
                        <StyledTableCell component="th" scope="row" className="item-label">
                          立会い者タイプ
                        </StyledTableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={data.supervisor_type || ''}
                              onChange={(e) => setData({ ...data, supervisor_type: e.target.value })}
                            >
                              <MenuItem value="顧客">顧客</MenuItem>
                              <MenuItem value="営業">営業</MenuItem>
                            </TextField>
                          ) : (
                            data.supervisor_type || '-'
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <StyledTableCell component="th" scope="row" className="item-label">
                          立会いタイミング
                        </StyledTableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={data.supervision_timing || ''}
                              onChange={(e) => setData({ ...data, supervision_timing: e.target.value })}
                            >
                              <MenuItem value="開始時">開始時</MenuItem>
                              <MenuItem value="終了時">終了時</MenuItem>
                              <MenuItem value="常時">常時</MenuItem>
                            </TextField>
                          ) : (
                            data.supervision_timing || '-'
                          )}
                        </TableCell>
                      </TableRow>
                    </>
                  )}

                  {/* ブレーカー情報 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      ブレーカー
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl>
                          <RadioGroup
                            row
                            value={data.breaker_status || ''}
                            onChange={(e) => setData({ ...data, breaker_status: e.target.value })}
                          >
                            <FormControlLabel value="動作確認済み" control={<Radio />} label="動作確認済み" />
                            <FormControlLabel value="未確認" control={<Radio />} label="未確認" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        data.breaker_status || '-'
                      )}
                    </TableCell>
                  </TableRow>
                  {data.breaker_status === '動作確認済み' && (
                    <TableRow>
                      <StyledTableCell component="th" scope="row" className="item-label">
                        ブレーカー説明
                      </StyledTableCell>
                      <TableCell>
                        {renderEditableField(
                          data.breaker_details,
                          (value) => setData({ ...data, breaker_details: value })
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* その他の作業条件 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      汚水廃棄場所
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.waste_location,
                        (value) => setData({ ...data, waste_location: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      その他(水源・電源)
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.utilities,
                        (value) => setData({ ...data, utilities: value })
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* 作業項目リスト */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">作業日時と従事者情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業日
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableDate(
                        data.work_date,
                        (newDate) => setData({
                          ...data,
                          work_date: newDate ? newDate.toISOString() : null
                        })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業開始時間
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableTime(
                        data.work_start_time,
                        (newTime) => setData({
                          ...data,
                          work_start_time: newTime ? newTime.format('HH:mm:ss') : null
                        })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業従事者
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.contractor_name,
                        (value) => setData({ ...data, contractor_name: value })
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      金額
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {renderEditableField(
                          data.amount,
                          (value) => setData({ ...data, amount: value }),
                          'text'
                        )}
                        <Typography sx={{ fontSize: '0.85rem' }}>円</Typography>
                      </Box>
                    </TableCell>

                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>
        </>
      )}
    </PageContainer>
  );
}

export default OrderFormDetail;